import { bunja } from 'bunja';
import { debounce } from 'es-toolkit';
import { atom } from 'jotai';
import { atomWithInfiniteQuery } from 'jotai-tanstack-query';

import { getFillHistoriesApi, getOrderHistoriesApi, getPositionHistoryListApi } from '@flipster/apis/v2/history';
import { fillHistoryInstance, orderHistoryInstance } from '@flipster/apis/v2/history/instance';
import { ProductType } from '@flipster/apis/v2/history/types';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { dateToEpochString } from '@flipster/utils/datetime';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

const defaultCount = 25;

const historyTypes = ['position', 'order', 'fill'] as const;
export type HistoryType = (typeof historyTypes)[number];

export const tradeHistoryBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { symbol: currentSymbol } = bunja.use(tradeSymbolBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);

  const tabAtom = atom<HistoryType>('position');
  const isCheckedCurrentMarketAtom = atom(false);
  const searchSymbolAtom = atom<string | undefined>(undefined);
  const dateRangeAtom = atom<{ startDate: Date; endDate: Date } | undefined>(undefined);

  const positionHistoryQueryAtom = atomWithInfiniteQuery((get) => {
    const tab = get(tabAtom);
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    const isCurrentMarket = get(isCheckedCurrentMarketAtom);
    const searchSymbol = get(searchSymbolAtom);
    const range = get(dateRangeAtom);
    const symbol = isCurrentMarket ? currentSymbol : searchSymbol;

    const startTime = range ? dateToEpochString(range.startDate) : undefined;
    const endTime = range ? dateToEpochString(range.endDate) : undefined;

    const dateRangeKey = `${startTime}-${endTime}`;

    return {
      queryKey: ['getPositionHistoryListApi', symbol, dateRangeKey],
      queryFn: ({ pageParam }) =>
        getPositionHistoryListApi({
          symbol,
          count: defaultCount,
          startTime,
          endTime,
          after: pageParam as string | undefined,
        }),
      enabled: isCookieLoggedIn && tab === 'position',
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => (lastPage.data.hasNext ? lastPage.data.history.at(-1)?.positionId : undefined),
      refetchOnMount: 'always',
    };
  });

  const orderHistoryQueryAtom = atomWithInfiniteQuery((get) => {
    const tab = get(tabAtom);
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    const isCurrentMarket = get(isCheckedCurrentMarketAtom);
    const searchSymbol = get(searchSymbolAtom);
    const range = get(dateRangeAtom);
    const symbol = isCurrentMarket ? currentSymbol : searchSymbol;

    const startTime = range ? dateToEpochString(range.startDate) : undefined;
    const endTime = range ? dateToEpochString(range.endDate) : undefined;

    const dateRangeKey = `${startTime}-${endTime}`;

    return {
      queryKey: ['getOrderHistoriesApi', symbol, dateRangeKey],
      queryFn: ({ pageParam }) =>
        getOrderHistoriesApi({
          symbol,
          productType: ProductType.Perpetual,
          count: defaultCount,
          startTime,
          endTime,
          after: pageParam as string | undefined,
        }),
      enabled: isCookieLoggedIn && tab === 'order',
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => (lastPage.data.hasNext ? lastPage.data.history.at(-1)?.orderId : undefined),
    };
  });

  const fillHistoryQueryAtom = atomWithInfiniteQuery((get) => {
    const tab = get(tabAtom);
    const isCookieLoggedIn = get(isCookieLoggedInAtom);
    const isCurrentMarket = get(isCheckedCurrentMarketAtom);
    const searchSymbol = get(searchSymbolAtom);
    const range = get(dateRangeAtom);
    const symbol = isCurrentMarket ? currentSymbol : searchSymbol;

    const startTime = range ? dateToEpochString(range.startDate) : undefined;
    const endTime = range ? dateToEpochString(range.endDate) : undefined;

    const dateRangeKey = `${startTime}-${endTime}`;

    return {
      queryKey: ['getFillHistoriesApi', symbol, dateRangeKey],
      queryFn: ({ pageParam }) =>
        getFillHistoriesApi({
          symbol,
          productType: ProductType.Perpetual,
          count: defaultCount,
          startTime,
          endTime,
          after: pageParam as string | undefined,
        }),
      enabled: isCookieLoggedIn && tab === 'fill',
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => (lastPage.data.hasNext ? lastPage.data.history.at(-1)?.execId : undefined),
    };
  });

  const positionHistoryAtom = atom((get) => {
    const query = get(positionHistoryQueryAtom);
    return query.data?.pages.flatMap((page) => page.data.history) ?? [];
  });

  const orderHistoryAtom = atom((get) => {
    const query = get(orderHistoryQueryAtom);
    return query.data?.pages.flatMap((page) => page.data.history.map(orderHistoryInstance)) ?? [];
  });

  const fillHistoryAtom = atom((get) => {
    const query = get(fillHistoryQueryAtom);
    return query.data?.pages.flatMap((page) => page.data.history.map(fillHistoryInstance)) ?? [];
  });

  const isTableLoadingAtom = atom((get) => {
    const positionHistory = get(positionHistoryQueryAtom);
    const orderHistory = get(orderHistoryQueryAtom);
    const fillHistory = get(fillHistoryQueryAtom);

    return positionHistory.isLoading || orderHistory.isLoading || fillHistory.isLoading;
  });

  const hasNextPageAtom = atom((get) => {
    const tab = get(tabAtom);
    switch (tab) {
      case 'position':
        return get(positionHistoryQueryAtom).hasNextPage;
      case 'order':
        return get(orderHistoryQueryAtom).hasNextPage;
      case 'fill':
        return get(fillHistoryQueryAtom).hasNextPage;
      default:
        return false;
    }
  });

  const isFetchingNextPageAtom = atom((get) => {
    const tab = get(tabAtom);
    switch (tab) {
      case 'position':
        return get(positionHistoryQueryAtom).isFetchingNextPage;
      case 'order':
        return get(orderHistoryQueryAtom).isFetchingNextPage;
      case 'fill':
        return get(fillHistoryQueryAtom).isFetchingNextPage;
      default:
        return false;
    }
  });

  // openPosition과 PeningPosition의 보유 개수와 포지션양을 감지할 수 있는 Atom (one way mode에서는 포지션 양이 변하기 때문)
  const positionSignatureAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    const pendingPositions = get(pendingPositionsAtom);

    const openKeys = Object.values(openPositions)
      .flat()
      .map((p) => `${p.symbol}-${p.slot}-${p.position}`)
      .sort()
      .join('/');

    const pendingKeys = Object.values(pendingPositions ?? {})
      .flat()
      .map((o) => `${o.symbol}-${o.orderId}`)
      .sort()
      .join('/');

    return `${openKeys}::${pendingKeys}`;
  });

  bunja.effect(() => {
    const debouncedCallback = debounce(() => {
      const tab = store.get(tabAtom);

      switch (tab) {
        case 'position': {
          const positionHistoryQuery = store.get(positionHistoryQueryAtom);
          positionHistoryQuery.refetch();
          break;
        }
        case 'order': {
          const orderHistoryQuery = store.get(orderHistoryQueryAtom);
          orderHistoryQuery.refetch();
          break;
        }
        case 'fill': {
          const fillHistoryQuery = store.get(fillHistoryQueryAtom);
          fillHistoryQuery.refetch();
          break;
        }
        default:
          break;
      }
    }, 1000);

    const unsubPositionSignature = store.sub(positionSignatureAtom, debouncedCallback);

    return () => {
      unsubPositionSignature();
    };
  });

  return {
    tabAtom,
    dateRangeAtom,
    positionHistoryAtom,
    orderHistoryAtom,
    fillHistoryAtom,
    isCheckedCurrentMarketAtom,
    searchSymbolAtom,
    isTableLoadingAtom,
    positionHistoryQueryAtom,
    orderHistoryQueryAtom,
    fillHistoryQueryAtom,
    hasNextPageAtom,
    isFetchingNextPageAtom,
    currentSymbol,
  };
});
tradeHistoryBunja.debugLabel = 'tradeHistoryBunja';
